// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { push } from 'react-router-redux';

import {
    AdsumLoader, AdsumWebMap, ArrowPathPatternOptions, DotPathBuilder, DotPathBuilderOptions, DotPathSectionDrawer,
    PinUserObject, SingleFloorAnimation, LabelTextObject
} from '@adactive/adsum-web-map';
import LoadingScreen, { LoadingScreenActions } from '@adactive/arc-loading-screen';
import ACA from '@adactive/adsum-utils/services/ClientAPI';

import { Map, WayfindingActions } from '@adactive/arc-map';
import WayFindingControls, { WayFindingControlsActions } from '@adactive/adsum-wayfindingcontrols-asia';
import selectionController from '@adactive/arc-map/src/controllers/SelectionController';
import placesController from '@adactive/arc-map/src/controllers/PlacesController';

import type { MapStateType } from '@adactive/arc-map/src/initialState';
import type { AppStateType } from './rootReducer';

import deviceConfig from './services/Config';
import store from './store/index';

import Header from './components/Header';
import MapControls from './components/MapControls';

import mapBackground from './assets/mapBackground.png';

import './App.css';

type MappedStatePropsType = {|
    mapState: MapStateType,
    currentSelectedObject: MapStateType,
    wayfindingState: *,
|};

type MappedDispatchPropsType = {|
    setPercentage: (percentage: ?number) => void,
    resetMapAndWayFinding: () => void,
    drawPathToPlace: (placeId: ?number, pmr: ?boolean) => void,
    setNewPlace: (action: ?string, placeId: ?number, pmr: ?boolean) => void,
|};

type OwnPropsType = {|
    pathName: string,
|};
type PropsType = MappedStatePropsType & MappedDispatchPropsType & OwnPropsType;


type StateType = {|
    mapLoaded: boolean,
    configLoaded: boolean,
    allFloors: [object],
    userFloor: object,
    deviceId: Number,
|};

const labelStyle = {
    arrival: {
        offset: {
            x: 0,
            y: 0,
            z: 2,
        },
        style: {
            backgroundColor: '#22419a',
            backgroundOpacity: 0.9,
            backgroundRadius: 10,
            color: '#ffffff',
            size: 60,
        },
    },
    interchange: {
        offset: {
            x: -20,
            y: 5,
            z: 2,
        },
        style: {
            backgroundColor: '#22419a',
            backgroundOpacity: 0.9,
            backgroundRadius: 10,
            color: '#ffffff',
            size: 60,
        },
    }
};

class App extends React.Component<PropsType, StateType> {
    state = {
        configLoaded: false,
        mapLoaded: false,
        allFloors: [],
        userFloor: null,
        action: null,
        actionId: null,
        pmr: false,
        showLift: false,
        pathDrawing: false,
        deviceId: 1158,
    };

    awmContainerRef = React.createRef();

    awm: ?AdsumWebMap = null;

    componentDidMount() {
        const { setPercentage } = this.props;

        const pathParams = this.getPathNameParams();

        deviceConfig.init()
            .then(() => { setPercentage(10); })
            .then(() => ACA.init(deviceConfig.config.api))
            .then(() => { setPercentage(25); })
            .then(() => ACA.load())
            .then(() => { setPercentage(75); })
            .then(() => {
                this.awm = new AdsumWebMap({
                    loader: new AdsumLoader({
                        entityManager: ACA.entityManager, // Give it in order to be used to consume REST API
                        deviceId: pathParams.deviceId || deviceConfig.config.map.deviceId, // The device Id to use
                        wireframeSpaces: true,
                        wireframeSpacesOptions: { color: 0x5a5b5a },
                    }), // The loader to use
                    camera: {
                        centerOnOptions: {
                            fitRatio: 3,
                            minDistance: 150, // to manage to zoom when click on store
                        },
                        centerOnPathSectionOptions: {
                            fitRatio: 3,
                        },
                    },
                    engine: {
                        container: this.awmContainerRef.current, // The div DOMElement to insert the canvas into
                    },
                    wayfinding: {
                        pathBuilder: new DotPathBuilder(new DotPathBuilderOptions({
                            patternSpace: 6,
                            patternSize: 4,
                            pattern: new ArrowPathPatternOptions({
                                color: '#be272f',
                            }),
                        })),
                        userObject: new PinUserObject({
                            color: '#be272f',
                            size: 25,
                        }),
                        pathSectionDrawer: new DotPathSectionDrawer({
                            center: true,
                            oriented: false,
                            speed: 1,
                            showDelay: 2000,
                        }),
                    },
                    scene: {
                        animation: new SingleFloorAnimation({
                            keepSiteVisible: false,
                            bounce: true,
                            center: true,
                            centerOnOptions: {
                                fitRatio: 1.7,
                                minDistance: 500,
                            },
                        }),
                    },
                });

                this.userObjectLabel = new LabelTextObject({
                    text: '  Start  ',
                    offset: {
                        x: 0,
                        y: 0,
                        z: 350,
                    },
                    style: {
                        size: 60,
                        lineHeight: 1.50,
                        color: '#be272f',
                        backgroundColor: 'white',
                        backgroundOpacity: '0.5',
                        backgroundRadius: 100,
                    },
                });

                setPercentage(90);
                this.setState({
                    configLoaded: true,
                    action: pathParams.action || 'drawToPlace',
                    actionId: pathParams.actionId || null,
                    pmr: pathParams.pmr || false,
                    deviceId: pathParams.deviceId || 1158
                });
            })
            .catch((err) => { console.error(err); });
    }

    componentDidUpdate(prevProps) {
        const {
            mapLoaded, configLoaded, actionId, pmr, pathDrawing,
        } = this.state;
        const {
            mapState, setPercentage, drawPathToPlace, wayfindingState
        } = this.props;
        if (configLoaded && !mapLoaded && mapState === 'idle') {
            const allFloors = this.getAllFloors();
            const userFloor = this.getUserFloor();

            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                mapLoaded: true,
                allFloors,
                userFloor,
            }, () => {
                setPercentage(100);
                if (actionId) {
                    const pois = ACA.getPoisFromPlace(actionId);
                    const poi = pois && pois[0];
                    const place = ACA.getPlace(actionId);
                    if (poi) {
                        poi.placeId = place && place.id;
                        drawPathToPlace(poi, [place], pmr);
                    }
                }
            });
        }

        if ((prevProps.wayfindingState.currentSectionIndex % 2) === 0 && (wayfindingState.currentSectionIndex % 2) === 0
            && prevProps.wayfindingState.drawing === true && wayfindingState.drawing === false
            && pathDrawing
            && actionId) {
            const theCurrentPath = placesController.getPath(actionId, pmr);
            if (theCurrentPath && theCurrentPath._pathSections && theCurrentPath._pathSections.length
                    && theCurrentPath._pathSections.length > 2 && wayfindingState.currentSectionIndex === 2) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({
                    pathDrawing: false,
                });
            } else if (theCurrentPath && theCurrentPath._pathSections && theCurrentPath._pathSections.length
                    && theCurrentPath._pathSections.length === 1 && wayfindingState.currentSectionIndex === 0) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({
                    pathDrawing: false,
                });
            }
        }

        if (prevProps.wayfindingState.currentSectionIndex !== wayfindingState.currentSectionIndex
            && !pathDrawing
            && wayfindingState.currentSectionIndex === 0) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                pathDrawing: true,
            });
        }
    }

    getUserFloor() {
        return this.awm.defaultFloor;
    }

    getAllFloors() {
        return this.awm.objectManager.floors;
    }

    getPathNameParams = (): PathParamsObjectType => {
        const { pathName } = this.props;
        const splittedPath = pathName.split('/');
        const deviceId = parseInt(splittedPath[1], 10);
        const action = splittedPath[2];
        const actionId = parseInt(splittedPath[3], 10);
        const pmr = splittedPath[4];
        return {
            deviceId,
            action,
            actionId,
            pmr: pmr === 'true' || false,
        };
    }

    getDrawPathSectionOptions = (pathSection) => {
        const isVertical = pathSection.isInterGround() && Math.abs(pathSection.getInclination()) > 30;

        return {
            drawOptions: {
                speed: isVertical ? 10 : 3,
                centerOnOptions: {
                    zoom: true,
                    altitude: isVertical ? 20 : 80,
                    time: 1500,
                    fitRatio: 3,
                    minDistance: isVertical ? 900 : 200,
                    maxDistance: isVertical ? 1000 : 400,
                },
            },
            setCurrentFloorOptions: {
                centerOnOptions: {
                    zoom: true,
                    altitude: isVertical ? 20 : 80,
                    time: 1500,
                },
            },
        };
    };

    onMapClick = (adsumObject) => {
        const { setNewPlace, resetMapAndWayFinding } = this.props;
        const {
            action, pmr, pathDrawing, deviceId
        } = this.state;
        if (!pathDrawing) {
            resetMapAndWayFinding(true, false, false, true);
            if (adsumObject && adsumObject.placeId && adsumObject.isSpace) {
                const place = ACA.getPlace(adsumObject.placeId);
                const pois = ACA.getPoisFromPlace(adsumObject.placeId);

                if (pois && pois.length) {
                    setNewPlace(deviceId, action, adsumObject.placeId, pmr);
                    selectionController.selectPlace(place);
                    this.setState({
                        action,
                        actionId: adsumObject.placeId,
                        pmr,
                    });
                }
            }
        }
    }

    takeMeThere = async () => {
        const { drawPathToPlace, resetMapAndWayFinding } = this.props;
        const { actionId, pmr, pathDrawing } = this.state;
        if (!pathDrawing) {
            const pois = ACA.getPoisFromPlace(actionId);
            const poi = pois && pois[0];
            const place = ACA.getPlace(actionId);
            if (poi) {
                poi.placeId = place && place.id;
                await resetMapAndWayFinding(true, false, true, true);
                drawPathToPlace(poi, [place], pmr);
            }
        }
    }

    viaLiftClick = async () => {
        const { resetMapAndWayFinding, setNewPlace } = this.props;
        const {
            deviceId, action, actionId, pmr, pathDrawing
        } = this.state;
        if (!pathDrawing) {
            const place = ACA.getPlace(actionId);
            await resetMapAndWayFinding(true, false, true, true);
            await setNewPlace(deviceId, action, actionId, pmr);
            if (place) await selectionController.selectPlace(place);
            this.setState({
                pmr: !pmr,
            });
        }
    }

    makeItBounceUp = () => {
        const { currentSelectedObject } = this.props;
        _.forEach(currentSelectedObject, (spaceObject) => {
            spaceObject.bounceUp(5);
        });
    }

    showLiftOption = (actionId) => {
        const { showLift } = this.state;

        if (actionId) {
            const pmrPath = placesController.getPath(actionId, true);
            if (pmrPath && pmrPath._pathSections && pmrPath._pathSections.length && pmrPath._pathSections.length > 2 && !showLift) {
                this.setState({
                    showLift: true,
                });
            } else if (pmrPath && pmrPath._pathSections && pmrPath._pathSections.length && pmrPath._pathSections.length === 1 && showLift) {
                this.setState({
                    showLift: false,
                });
            }
        } else if (showLift) {
            this.setState({
                showLift: false,
            });
        }
    }

    isMapOpen() { // eslint-disable-line class-methods-use-this
        // Depending on your project, feel free to change the behavior depending on your pathName prop value
        return true;
    }

    renderMap(): Map {
        const { currentSelectedObject } = this.props;
        const { allFloors, userFloor, pathDrawing } = this.state;
        if (this.awm && this.awm.cameraManager && this.awm.cameraManager.control) {
            this.awm.cameraManager.control.enableRotate = false;
        }

        if (currentSelectedObject.length) {
            this.makeItBounceUp();
        }

        return (
            <Map
                backgroundImage={mapBackground}
                store={store}
                awm={this.awm}
                isOpen
                className="app-map-container"
                onClick={this.onMapClick}
                userObjectLabel={this.userObjectLabel}
                getDrawPathSectionOptions={this.getDrawPathSectionOptions}
                zoom={{
                    min: 50,
                    max: 1000,
                }}
                autoSelectOnClick={false}
                highlightColor="#464241"
            >
                <WayFindingControls
                    awm={this.awm}
                    kioskPlace={userFloor}
                    arrivalLabelStyle={labelStyle.arrival}
                    interchangeLabelStyle={labelStyle.interchange}
                    pmrNaming
                    destinationLabelText=" "
                    icLabelText=" Take "
                />
                <div id="adsum-web-map-container" ref={this.awmContainerRef}>
                    <MapControls allFloors={allFloors} userFloor={userFloor} pathDrawing={pathDrawing} />
                </div>
            </Map>
        );
    }

    render(): React.Element<'div'> {
        const {
            userFloor, pmr, actionId, showLift, pathDrawing
        } = this.state;
        this.showLiftOption(actionId);
        return (
            <div className="app">
                <LoadingScreen />

                <Header userFloor={userFloor} pathDrawing={pathDrawing} />

                { this.renderMap() }

                <div className="take-me-there-mobile-container" onClick={this.takeMeThere}>
                    <div className="take-me-there-mobile-btn" style={{ opacity: pathDrawing ? '0.5' : '1' }}>Take Me There</div>
                </div>

                { !showLift ? null : (
                    <div
                        className="via-lift-mobile-container"
                        onClick={this.viaLiftClick}
                        style={{ opacity: pathDrawing ? '0.5' : '1' }}
                    >
                        { pmr ? <div className="via-lift-active" /> : <div className="via-lift-inactive" /> }
                        <input type="checkbox" />
                    Via Lift
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: AppStateType): MappedStatePropsType => ({
    mapState: state.map.state,
    pathName: state.routing.location.pathname,
    currentSelectedObject: state.map.currentSelectedObject,
    wayfindingState: state.map.wayfindingState,
});

const mapDispatchToProps = (dispatch: *): MappedDispatchPropsType => ({
    setPercentage: (percentage: ?number): void => {
        dispatch(LoadingScreenActions.setPercentage(percentage));
    },
    resetMapAndWayFinding: (reset, resetMap, resetMapAnimatedOption, resetWayfinding) => {
        dispatch(WayFindingControlsActions.resetMapAndWayFinding(
            reset,
            resetMap,
            resetMapAnimatedOption,
            resetWayfinding
        ));
    },
    drawPathToPlace: (poi, poiPlace, pmr: ?boolean) => {
        dispatch(WayFindingControlsActions.tmtt(poi, poiPlace, pmr));
    },
    setNewPlace: (deviceId: number, action: string, placeId: number, pmr: ?boolean) => {
        dispatch(push(`/${deviceId}/${action}/${placeId}/${pmr}`));
        dispatch(WayfindingActions.willResetPathAction());
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(App);



// WEBPACK FOOTER //
// ./src/App.js